import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';
import type { Language } from '@src/types.ts/language';

// A voice "speaks" a language when the language is in its (advisory, DI19)
// supported set. Drives which voices are eligible as a per-language default.
export const voiceSpeaksLanguage = (
  voice: AiVoiceRecord,
  language: Language,
): boolean =>
  voice.supportedLanguages?.some(l => l.id === language.id) ?? false;

export interface LanguageDefaultResolution {
  /** The speaker's ordered voices that speak this language (selectable pool). */
  speaking: AiVoiceRecord[];
  /** The pinned voice id if it's valid for this language, else ''. */
  validPinned: string;
  /** The resolved default voice id: valid pin → first speaking voice → ''. */
  resolvedVoiceId: string;
}

// Resolve the speaker's default AI Voice for one language (DI19), shared by the
// Manage modal's Languages tab (which sets the pin) and the view-only pop-over
// tab (which badges "Default"). The two MUST agree, so the rule lives here:
// a valid per-language pin wins, else the first ordered voice that speaks the
// language, else unassigned.
export const resolveLanguageDefault = (
  orderedVoices: AiVoiceRecord[],
  languagePrefs: Record<string, string>,
  language: Language,
): LanguageDefaultResolution => {
  const speaking = orderedVoices.filter(v => voiceSpeaksLanguage(v, language));
  const pinned = languagePrefs[language.id] ?? '';
  const validPinned = speaking.some(v => v.id === pinned) ? pinned : '';
  return {
    speaking,
    validPinned,
    resolvedVoiceId: validPinned || speaking[0]?.id || '',
  };
};
